'use client';

/* ──────────────────────────────────────────────
 *  Cible de portal pour les surcouches du tunnel
 *  (lightbox photo, modale vidéo, fiche véhicule).
 *
 *  Ces surcouches NE PEUVENT PAS rester dans `.bt-root` :
 *  la racine déclare `container: bt / inline-size`
 *  (layout containment), ce qui ferait d'elle le
 *  containing block d'un backdrop `position: fixed`
 *  (voile partiel, centrage cassé).
 *
 *  Mais `<body>` en dur casse le widget embarqué
 *  (`propelsia-widgets`) : le tunnel y est monté dans un
 *  Shadow Root et la feuille de style est injectée DANS
 *  ce shadow. Un portal sur `<body>` en sort — la
 *  surcouche atterrit sans une seule règle CSS, c'est-à-dire
 *  une image brute empilée en bas de la page hôte.
 *
 *  On résout donc la cible depuis l'ancre : hors shadow
 *  DOM `<body>` (comportement historique inchangé), et
 *  dans un shadow root un conteneur dédié accroché à la
 *  racine du shadow — hors de `.bt-root`, donc `fixed`
 *  reste bien relatif au viewport, et les styles du
 *  shadow s'appliquent.
 * ────────────────────────────────────────────── */

import { useMemo, type RefObject } from 'react';

const OVERLAY_ROOT_ATTRIBUTE = 'data-bt-overlay-root';

/** Résout (et crée au besoin) le nœud hôte des surcouches pour l'arbre DOM de `anchor`. */
export function resolveOverlayPortalTarget(anchor: Element | null | undefined): HTMLElement | null {
  if (typeof document === 'undefined') return null;

  const root = anchor?.getRootNode();
  if (typeof ShadowRoot === 'undefined' || !(root instanceof ShadowRoot)) return document.body;

  const existing = root.querySelector<HTMLElement>(`[${OVERLAY_ROOT_ATTRIBUTE}]`);
  if (existing) return existing;

  const container = document.createElement('div');
  container.setAttribute(OVERLAY_ROOT_ATTRIBUTE, '');
  root.appendChild(container);
  return container;
}

/**
 * Cible de portal pour une surcouche ouverte à la demande.
 *
 * `null` tant que `active` est faux : les appelants ne rendent la surcouche qu'après
 * une interaction, donc `anchorRef` est déjà monté quand `active` passe à vrai et la
 * cible se résout de façon SYNCHRONE dans le même rendu (SSR-safe : jamais actif côté
 * serveur). Résoudre dans un effet décalait le montage d'un tick : l'effet de focus de
 * la lightbox (`[isOpen]`) tournait sur une dialog pas encore montée et ne rejouait
 * jamais — le focus restait derrière le voile, sans piège Tab. La résolution est
 * idempotente (querySelector du conteneur existant), donc sûre en StrictMode.
 */
export function useOverlayPortalTarget(anchorRef: RefObject<Element | null>, active: boolean): HTMLElement | null {
  return useMemo(
    (): HTMLElement | null => (active ? resolveOverlayPortalTarget(anchorRef.current) : null),
    [active, anchorRef],
  );
}
